// The schedule read and its cache key. The desktop branch and the connection
// toast stay in the delegate and cross as ports.
import { logger, sanitizeError } from '@heidi/runtime/logger';
import { skipToken, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { buildScheduleQueryKey } from '../../contracts/queryKeys';
import type { GetIntegrationsScheduleType } from '../../contracts/types';
import { getIntegrationsSchedule } from '../api/schedule';
import { isEhrConnectionError } from '../ehr-connection-error';

type DesktopScheduleFetch =
  | { handled: false }
  | { handled: true; schedule: GetIntegrationsScheduleType };

type Params<TSelected> = {
  start_date?: string;
  end_date?: string;
  filter?: string[];
  enabled?: boolean;
  select?: (data: GetIntegrationsScheduleType) => TSelected;
  integrationEhrProvider?: string;
  isIntegrationOnboardingSession?: boolean;
  canSyncSchedule?: boolean;
  hasMultiple?: boolean;
  integrationOnboardingSession: unknown;
  fetchFromDesktop: (input: {
    start_date: string;
    end_date: string;
    integrationEhrProvider: string;
  }) => Promise<DesktopScheduleFetch>;
  onConnectionError: (error: AxiosError) => void;
};

export function useQueryIntegrationsMixinGetSchedule<
  TSelected = GetIntegrationsScheduleType,
>({
  start_date,
  end_date,
  filter,
  enabled = true,
  select,
  integrationEhrProvider,
  isIntegrationOnboardingSession,
  canSyncSchedule,
  hasMultiple,
  integrationOnboardingSession,
  fetchFromDesktop,
  onConnectionError,
}: Params<TSelected>) {
  return useQuery<GetIntegrationsScheduleType, Error, TSelected>({
    queryKey: buildScheduleQueryKey({
      start_date,
      end_date,
      integrationEhrProvider,
      filter,
    }),
    select,
    enabled: Boolean(
      enabled && isIntegrationOnboardingSession && (canSyncSchedule || hasMultiple)
    ),
    retry: false,
    queryFn:
      start_date && end_date && integrationEhrProvider && integrationOnboardingSession
        ? async () => {
            const desktopSchedule = await fetchFromDesktop({
              start_date,
              end_date,
              integrationEhrProvider,
            });
            if (desktopSchedule.handled) {
              return desktopSchedule.schedule;
            }

            try {
              const { data } = await getIntegrationsSchedule({
                start_date,
                end_date,
                filter,
              });
              return data.schedule;
            } catch (error) {
              if (isEhrConnectionError(error)) {
                onConnectionError(error);
              }

              if (error instanceof AxiosError && error.response?.status === 400) {
                logger.error('Schedule fetch failed with 400', {
                  error: sanitizeError(error),
                });
              }

              throw error;
            }
          }
        : skipToken,
  });
}
